import { lazy, Suspense, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { ErrorBoundary } from "react-error-boundary";
import { QueryErrorResetBoundary } from "@tanstack/react-query";
import { useRadarOnboardingStore } from "@modules/radar/shared/store/radar-onboarding.store";
import { useHasModule } from "@shared/access-control/useHasModule";
import { sidekickModuleEnum } from "@shared/access-control/sidekick-modules";
import { useGetPreferences } from "@shared/generated/radar/queries/usersController/useGetPreferences";
import { appLogger } from "@shared/services/app-logger";

const RadarOnboardingDialog = lazy(() =>
  import("./RadarOnboardingDialog").then((module) => ({
    default: module.RadarOnboardingDialog,
  })),
);

function RadarOnboardingGateContent() {
  const hasRadar = useHasModule(sidekickModuleEnum.RADAR);
  const isOpen = useRadarOnboardingStore((state) => state.isOpen);
  const open = useRadarOnboardingStore((state) => state.open);
  const close = useRadarOnboardingStore((state) => state.close);
  const savedPreferences = useGetPreferences({
    // throwOnError hands fetch failures to the boundary in RadarOnboardingGate below.
    query: { enabled: hasRadar, throwOnError: true },
  });

  // Open once when no preferences are saved; refetches after that never close it.
  useEffect(() => {
    if (savedPreferences.data?.preferences === null) open();
  }, [savedPreferences.data, open]);

  if (!hasRadar) return null;

  return (
    <Suspense fallback={null}>
      <RadarOnboardingDialog
        isOpen={isOpen}
        onClose={close}
        preferences={savedPreferences.data?.preferences ?? null}
      />
    </Suspense>
  );
}

// Mounted in the main layout so onboarding isn't tied to /signals. Boundary-scoped so a
// preferences fetch failure can't take down the rest of the app; resets on navigation to retry.
// QueryErrorResetBoundary's reset clears the query's own cached error first, so the remount
// actually attempts a fresh fetch instead of immediately re-throwing the same failure.
export function RadarOnboardingGate() {
  const { pathname } = useLocation();

  return (
    <QueryErrorResetBoundary>
      {({ reset }) => (
        <ErrorBoundary
          resetKeys={[pathname]}
          onReset={reset}
          fallback={null}
          onError={(error) =>
            appLogger.error(
              "[RadarOnboardingGate] failed to load preferences",
              { error },
            )
          }
        >
          <RadarOnboardingGateContent />
        </ErrorBoundary>
      )}
    </QueryErrorResetBoundary>
  );
}
